'use client';

import ModalProfile from '@/components/sections/builder/elements/body/customer/customer_detail/components/modal-profile';
import { CustomerDetailElementDataType } from '@/components/sections/builder/elements/body/customer/customer_detail/type';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { dummyCustomerDetail } from '@/constant/dummyData/dummy-customer-detail';
import useModal from '@/hooks/useModal';
import { updateProfile } from '@/lib/actions/customer/action';
import { queryClient } from '@/lib/query-client';
import { StoreCustomerProfilePayload } from '@/types/http';
import { BaseCustomerDetail } from '@/types/http/customer/common';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import Cookies from 'js-cookie';

export default function ELementContentCustomerDetail({
  element,
  data,
  preview,
  host,
}: ElementProps) {
  const token = Cookies.get('accessToken');
  const content = element.data as CustomerDetailElementDataType;
  const modalProfile = useModal<{
    data?: BaseCustomerDetail;
  }>();
  const profile = preview ? dummyCustomerDetail : (data as BaseCustomerDetail);

  const handleOpenModal = () => {
    modalProfile.open({
      data: {
        first_name: profile?.first_name as string,
        last_name: profile?.last_name as string,
        dob: profile?.dob ? new Date(profile?.dob as string) : new Date(),
        gender: profile?.gender as string,
        email: profile?.email as string,
        phone: profile?.phone as string,
      },
    });
  };

  const handleEditProfile = async (values: StoreCustomerProfilePayload) => {
    try {
      const payload = {
        ...values,
        dob: values?.dob ? format(new Date(values?.dob as Date), 'yyyy-MM-dd') : new Date(),
      };

      const result = await updateProfile(host as string, token as string, payload);

      if (result.success) {
        toast({
          title: 'Success',
          variant: 'success',
          description: result.message || 'Profile has been successfully updated',
        });
        queryClient.invalidateQueries({ queryKey: ['customer_profile'] });
      } else {
        toast({
          title: 'Error',
          variant: 'destructive',
          description: result.message || 'Something went wrong.',
        });
      }
      modalProfile.setIsShow(false);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast({
          title: 'Error',
          variant: 'destructive',
          description: error.response?.data.message || 'Something went wrong.',
        });
      }
    }
  };

  return (
    <>
      <div className='bg_scheme_customer_detail customer_detail_padding mx-auto w-full md:container xl:min-h-[calc(100vh_-_300px)]'>
        <div className='theme_layout_page_width mx-auto flex w-full flex-col gap-3 px-4'>
          <div className='flex items-center justify-between'>
            <h1 className='text-xl font-bold'>My Profile</h1>
            <Button
              variant='link'
              style={{
                backgroundColor: content.colorScheme?.btnSolidBackgroud,
                color: content.colorScheme?.btnSolidlabel,
              }}
              onClick={handleOpenModal}
              data-testid='edit-profile-button'
            >
              Edit profile
            </Button>
          </div>
          <Card className='w-full'>
            <CardContent className='space-y-6'>
              <h2 className='mt-5 text-lg font-semibold'>Bio</h2>
              <div className='space-y-2 text-base'>
                <div className='grid w-full grid-cols-4 items-center gap-4'>
                  <Label className='text-base font-normal'>Name</Label>
                  <span className='col-span-3'>
                    {profile?.first_name as string} {profile?.last_name as string}
                  </span>
                </div>
                <div className='grid w-full grid-cols-4 items-center gap-4'>
                  <Label className='text-base font-normal'>Date of Birth</Label>
                  <span className='col-span-3'>
                    {(profile?.dob as string) && format(profile?.dob as string, 'dd MMMM yyyy')}
                  </span>
                </div>
                <div className='grid w-full grid-cols-4 items-center gap-4'>
                  <Label className='text-base font-normal'>Gender</Label>
                  <span className='col-span-3'>{profile?.gender as string}</span>
                </div>
              </div>
              <h3 className='text-lg font-semibold'>Contact</h3>
              <div className='space-y-2 text-base'>
                <div className='grid w-full grid-cols-4 items-center gap-4'>
                  <Label className='text-base font-normal'>Email</Label>
                  <span className='col-span-3'>{profile?.email as string}</span>
                </div>
                <div className='grid w-full grid-cols-4 items-center gap-4'>
                  <Label className='text-base font-normal'>Phone</Label>
                  <span className='col-span-3'>{profile?.phone as string}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <ModalProfile
        openModal={modalProfile.isShow}
        setOpenModal={modalProfile.setIsShow}
        data={modalProfile.data?.data}
        onSave={handleEditProfile}
        content={content}
      />
      <style jsx>
        {`
          .bg_scheme_customer_detail {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          .customer_detail_padding {
            padding-top: ${content.topPadding}px;
            padding-bottom: ${content.bottomPadding}px;
          }
        `}
      </style>
    </>
  );
}
